// src/components/MAP/hooks/useTimeSync.js
import { useCallback } from 'react';

export const useTimeSync = (timelineData, polylinesRef, markersRef, mapInstance, routes) => {
  const handleSliderChange = useCallback((value) => {
    // Add null/undefined check
    if (value === undefined || value === null) {
      console.warn('useTimeSync: Received invalid time value, ignoring update');
      return;
    }

    if (!mapInstance.current) return;
    
    timelineData.forEach(({ index, routeLatLngs, timeOffsets }) => {
      // Check if route exists and is configured
      const route = routes[index];
      if (!route) return;
      
      // Find the closest time index for the current value
      let closestIdx = findClosestTimeIndex(timeOffsets, value);
      
      // Safety check to prevent out of bound access
      if (closestIdx < 0) closestIdx = 0;
      if (closestIdx >= routeLatLngs.length) closestIdx = routeLatLngs.length - 1;
      
      // Get all path points up to current time
      const updatedPolylinePoints = routeLatLngs.slice(0, closestIdx + 1);

      // Update dynamic polyline
      const polyline = polylinesRef.current[index];
      if (polyline) {
        polyline.setLatLngs(updatedPolylinePoints);
      }

      // Update marker position
      const marker = markersRef.current[index];
      const markerLatLng = routeLatLngs[closestIdx];
      
      if (marker && markerLatLng) {
        // Update position regardless of marker visibility
        marker.setLatLng(markerLatLng);
        
        // Set marker visibility based on route visibility
        const shouldBeVisible = route.visible;
        const isCurrentlyVisible = mapInstance.current.hasLayer(marker);
        
        // Only change visibility when needed
        if (shouldBeVisible && !isCurrentlyVisible) {
          mapInstance.current.addLayer(marker);
        } else if (!shouldBeVisible && isCurrentlyVisible) {
          mapInstance.current.removeLayer(marker);
        }
      }
    });
  }, [timelineData, polylinesRef, markersRef, mapInstance, routes]);
  
  // Helper function to find the closest time index
  const findClosestTimeIndex = (timeOffsets, targetTime) => {
    if (!timeOffsets || !timeOffsets.length) return 0;
    
    // Handle edge cases
    if (targetTime <= timeOffsets[0]) return 0;
    if (targetTime >= timeOffsets[timeOffsets.length - 1]) return timeOffsets.length - 1;
    
    // Binary search for closest time
    let left = 0;
    let right = timeOffsets.length - 1;
    
    while (left <= right) {
      const mid = Math.floor((left + right) / 2);
      
      // Exact match
      if (timeOffsets[mid] === targetTime) return mid;
      
      if (timeOffsets[mid] < targetTime) {
        left = mid + 1;
      } else {
        right = mid - 1;
      }
    }
    
    // After binary search, left and right will be the indexes flanking the target time
    const beforeIdx = right;
    const afterIdx = left;
    
    // If we've gone outside the array bounds, return the closest valid index
    if (afterIdx >= timeOffsets.length) return beforeIdx;
    if (beforeIdx < 0) return afterIdx;
    
    // Return the closest index based on distance
    return (targetTime - timeOffsets[beforeIdx]) < (timeOffsets[afterIdx] - targetTime) 
      ? beforeIdx 
      : afterIdx;
  };

  return { handleSliderChange };
};